import React, { useState } from "react";
import {
  Button,
  Card,
  Row,
  Col,
  Tab,
  Tabs,
  Form,
  FormControl,
} from "react-bootstrap";
import { ColumnDef } from "@tanstack/react-table";
import CurrencyFormat from "react-currency-format";
import DataTable from "@components/DataTable";
import SlideoutMenu from "@components/SlideOutMenuV2/SlideOutMenu";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";
import SeafoamButton from "@components/Buttons/SeafoamButton/SeafoamButton";
import SubmitButton from "@components/Buttons/SubmitButton/SubmitButton";
import { handleAlert } from "@utils/helperFunctions";
import ToastAlert from "@components/Alerts/ToastAlert/ToastAlert";
import { PsPricingRow } from "../types/PsPricingRow";
import useStyles from "../styles/useStyles";

interface TableSection {
  title: string;
  rows: PsPricingRow[];
}

interface ByLobData {
  profRows: PsPricingRow[];
  lobTables: TableSection[];
  govRows: PsPricingRow[];
}

interface ByPhaseData {
  phaseTables: TableSection[];
  govRows: PsPricingRow[];
}

interface ByServiceData {
  phaseTables: TableSection[];
  govRows: PsPricingRow[];
}

interface ByLocationData {
  locTables: TableSection[];
}

interface Props {
  currencyUnit: string;
  rateType: string;
  projectId: string;
  projectStatus: string;
  pricingPrivilege: boolean;
  oneTimeAdjustment: number;
  priceAdjustment: string;
  setPriceAdjustment: (v: string) => void;
  onSubmitAdjustment: () => void;
  adjustmentError: string[] | null;
  setAdjustmentError: (v: string[] | null) => void;
  paymentTerms: any[];
  currentPaymentTerm: any;
  newPaymentTermId: string | null;
  setNewPaymentTermId: (v: string | null) => void;
  onSubmitPaymentTerm: () => void;
  paymentTermError: string[] | null;
  setPaymentTermError: (v: string[] | null) => void;
  byLobData: ByLobData;
  byPhaseData: ByPhaseData;
  byServiceData: ByServiceData;
  byLocationData: ByLocationData;
  showServiceTab: boolean;
  showLocationTab: boolean;
  isLoading: boolean;
}

const Show: React.FC<Props> = ({
  currencyUnit,
  rateType,
  projectId,
  projectStatus,
  pricingPrivilege,
  oneTimeAdjustment,
  priceAdjustment,
  setPriceAdjustment,
  onSubmitAdjustment,
  adjustmentError,
  setAdjustmentError,
  paymentTerms,
  currentPaymentTerm,
  newPaymentTermId,
  setNewPaymentTermId,
  onSubmitPaymentTerm,
  paymentTermError,
  setPaymentTermError,
  byLobData,
  byPhaseData,
  byServiceData,
  byLocationData,
  showServiceTab,
  showLocationTab,
  isLoading,
}) => {
  const styles = useStyles();
  const [showTerms, setShowTerms] = useState(false);

  const formatCurrency = (value: number) => (
    <CurrencyFormat
      displayType="text"
      prefix={currencyUnit}
      isNumericString={true}
      thousandSeparator={true}
      value={value.toFixed(2)}
    />
  );

  const isSummary = (row: PsPricingRow) =>
    row.rowType === "total" || row.rowType === "margin";

  const hoursLabel =
    rateType !== "Hours" ? rateType : "Extended Hours";

  const buildColumns = (
    firstColumnName: string,
    showAdjustment = false
  ): ColumnDef<PsPricingRow>[] => [
    {
      header: firstColumnName,
      cell(props) {
        const row = props.row.original;
        if (row.rowType === "adjustment")
          return <span style={{ fontWeight: 700 }}>Price Adjustment</span>;
        if (row.rowType === "total") return null;
        if (row.rowType === "margin") return null;
        if (row.rowType === "serviceTotal")
          return (
            <span style={{ fontWeight: 700, paddingLeft: 20 }}>
              {row.resource}
            </span>
          );
        return (
          <span
            style={{
              paddingLeft: row.rowType === "subservice" ? 20 : 0,
            }}
          >
            {row.rowType === "subservice"
              ? `- ${row.resource}`
              : row.resource}
          </span>
        );
      },
    },
    {
      header: hoursLabel,
      cell(props) {
        const row = props.row.original;
        if (row.rowType === "adjustment") return null;
        if (row.rowType === "margin") return null;
        if (row.rowType === "total")
          return (
            <span style={{ fontWeight: 700 }}>
              {row.hours > 0 ? row.hours.toFixed(2) : ""}
            </span>
          );
        if (row.rowType === "serviceTotal")
          return (
            <span style={{ fontWeight: 700 }}>
              {row.hours > 0 ? row.hours.toFixed(2) : ""}
            </span>
          );
        return <span>{row.hours > 0 ? row.hours.toFixed(2) : ""}</span>;
      },
    },
    {
      header: "Effective Rate",
      cell(props) {
        const row = props.row.original;
        if (
          row.rowType === "adjustment" ||
          row.rowType === "margin" ||
          row.rowType === "total" ||
          row.rowType === "serviceTotal"
        )
          return null;
        if (row.revenue === 0 || row.hours === 0 || isNaN(row.rate))
          return <span>---</span>;
        return <span>{formatCurrency(row.rate)}</span>;
      },
    },
    {
      header: "Revenue",
      cell(props) {
        const row = props.row.original;
        if (row.rowType === "adjustment") {
          if (
            showAdjustment &&
            projectStatus === "building" &&
            pricingPrivilege
          ) {
            return (
              <Form
                onSubmit={(e: React.FormEvent) => {
                  e.preventDefault();
                  onSubmitAdjustment();
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                  }}
                >
                  <FormControl
                    type="number"
                    step="0.01"
                    style={{ maxWidth: 120, textAlign: "right" }}
                    value={priceAdjustment}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setPriceAdjustment(e.target.value)
                    }
                  />
                  <SeafoamButton
                    style={{ height: "2.5em", marginLeft: "0.5em" }}
                    className="btn-sm"
                    text="Update"
                    type="submit"
                    onClick={undefined}
                    disabled={false}
                  />
                </div>
              </Form>
            );
          }
          return <span>{formatCurrency(oneTimeAdjustment)}</span>;
        }
        if (row.rowType === "margin") return null;
        if (row.rowType === "total")
          return (
            <span style={{ fontWeight: 700 }}>
              {formatCurrency(row.revenue)}
            </span>
          );
        if (row.rowType === "serviceTotal")
          return (
            <span style={{ fontWeight: 700 }}>
              {formatCurrency(row.revenue)}
            </span>
          );
        return <span>{formatCurrency(row.revenue)}</span>;
      },
    },
    {
      header: "Effective Cost",
      cell(props) {
        const row = props.row.original;
        if (
          row.rowType === "adjustment" ||
          row.rowType === "margin" ||
          row.rowType === "total" ||
          row.rowType === "serviceTotal"
        )
          return null;
        if (row.totalCost === 0 || row.hours === 0 || isNaN(row.effectiveCost))
          return <span>---</span>;
        return <span>{formatCurrency(row.effectiveCost)}</span>;
      },
    },
    {
      header: "Total Cost",
      cell(props) {
        const row = props.row.original;
        if (row.rowType === "adjustment" || row.rowType === "margin")
          return null;
        if (row.rowType === "total")
          return (
            <span style={{ fontWeight: 700, color: "#b90200" }}>
              {formatCurrency(row.totalCost)}
            </span>
          );
        if (row.rowType === "serviceTotal")
          return (
            <span style={{ fontWeight: 700 }}>
              {formatCurrency(row.totalCost)}
            </span>
          );
        return <span>{formatCurrency(row.totalCost)}</span>;
      },
    },
    {
      header: "Gross Profit",
      cell(props) {
        const row = props.row.original;
        if (row.rowType === "adjustment")
          return <span>{formatCurrency(oneTimeAdjustment)}</span>;
        if (row.rowType === "margin")
          return (
            <span style={{ fontWeight: 700, color: "#418172" }}>
              {row.revenue ? `${row.revenue.toFixed(2)}%` : "0.00%"}
            </span>
          );
        if (row.rowType === "total")
          return (
            <span style={{ fontWeight: 700, color: "#418172" }}>
              {formatCurrency(row.grossProfit)}
            </span>
          );
        if (row.rowType === "serviceTotal")
          return (
            <span style={{ fontWeight: 700 }}>
              {formatCurrency(row.grossProfit)}
            </span>
          );
        return <span>{formatCurrency(row.grossProfit)}</span>;
      },
    },
  ];

  const renderTable = (
    title: string,
    rows: PsPricingRow[],
    firstColumnName = "Resource",
    showAdjustment = false
  ) => (
    <Card className={styles.card} key={title}>
      <Card.Header className={styles.cardHeader}>
        <Row className={styles.headerRow}>
          <Col className={styles.title}>{title}</Col>
        </Row>
      </Card.Header>
      <hr className={styles.hr} />
      <Card.Body>
        <div className={styles.rightAlignColumns}>
          <DataTable
            data={rows}
            columns={buildColumns(firstColumnName, showAdjustment)}
            selectable={false}
            striped
            hover
          />
        </div>
      </Card.Body>
    </Card>
  );

  // Inject adjustment row into profRows for By LOB
  const profRowsWithAdj = (() => {
    const rows = [...byLobData.profRows];
    if (oneTimeAdjustment !== 0) {
      // Insert before TOTAL and MARGIN
      const totalIdx = rows.findIndex((r) => r.rowType === "total");
      if (totalIdx >= 0) {
        rows.splice(totalIdx, 0, {
          id: "adjustment",
          resource: "Price Adjustment",
          hours: 0,
          rate: 0,
          revenue: oneTimeAdjustment,
          effectiveCost: 0,
          totalCost: 0,
          grossProfit: oneTimeAdjustment,
          rowType: "adjustment",
        });
      }
    }
    return rows;
  })();

  return (
    <div className={styles.page}>
      {handleAlert(
        !!adjustmentError,
        adjustmentError || [],
        () => setAdjustmentError(null),
        "warning",
        ToastAlert
      )}
      {handleAlert(
        !!paymentTermError,
        paymentTermError || [],
        () => setPaymentTermError(null),
        "warning",
        ToastAlert
      )}
      {isLoading ? (
        <ScopeStackSpinner />
      ) : (
        <>
          <SlideoutMenu
            isOpen={showTerms}
            onClose={() => setShowTerms(false)}
            onSave={() => {
              onSubmitPaymentTerm();
              setShowTerms(false);
            }}
            title="Professional Service Terms"
            actionBtnText="Save"
          >
            <Form.Label>Payment Term</Form.Label>
            <div className="formGroup">
              <select
                className="form-control"
                value={newPaymentTermId || ""}
                onChange={(
                  e: React.ChangeEvent<HTMLSelectElement>
                ) => setNewPaymentTermId(e.target.value)}
              >
                {paymentTerms.map((term) => (
                  <option key={term.id} value={term.id}>
                    {term.attributes.name}
                  </option>
                ))}
              </select>
            </div>
          </SlideoutMenu>

          <Card className={styles.card}>
            <Card.Header className={styles.cardHeader}>
              <Row className={styles.headerRow}>
                <Col>
                  <div className={styles.title}>
                    Professional Service Pricing
                  </div>
                  <div className={styles.subtitle}>
                    <span style={{ fontWeight: 700 }}>Payment Term: </span>
                    {currentPaymentTerm?.attributes?.name || "—"}
                  </div>
                </Col>
                <Col
                  className="text-end"
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "center",
                  }}
                >
                  <Button
                    className="btnSeawhite"
                    onClick={() => setShowTerms(true)}
                  >
                    Payment Terms
                  </Button>
                </Col>
              </Row>
            </Card.Header>
            <hr className={styles.hr} style={{ marginBottom: 4 }} />
            <Card.Body className={styles.pricingTabs}>
              <Tabs defaultActiveKey="byLob" className="mb-3">
                <Tab eventKey="byLob" title="By LOB">
                  <div className={styles.tabContent}>
                    {renderTable(
                      "Professional Service Resource Summary",
                      profRowsWithAdj,
                      "Resource",
                      true
                    )}

                    {byLobData.lobTables.map((t) =>
                      renderTable(t.title, t.rows)
                    )}
                  </div>
                </Tab>

                <Tab eventKey="byPhase" title="By Phase">
                  <div className={styles.tabContent}>
                    {byPhaseData.phaseTables.map((t) =>
                      renderTable(t.title, t.rows)
                    )}
                  </div>
                </Tab>

                {showServiceTab && (
                  <Tab eventKey="byService" title="By Service">
                    <div className={styles.tabContent}>
                      {byServiceData.phaseTables.map((t) =>
                        renderTable(t.title, t.rows, "Service")
                      )}
                    </div>
                  </Tab>
                )}

                {showLocationTab && (
                  <Tab eventKey="byLocation" title="By Location">
                    <div className={styles.tabContent}>
                      {byLocationData.locTables.map((t) =>
                        renderTable(t.title, t.rows)
                      )}
                    </div>
                  </Tab>
                )}
              </Tabs>

              {renderTable(
                "Project Governance Pricing",
                byLobData.govRows
              )}
            </Card.Body>
          </Card>
        </>
      )}
    </div>
  );
};

export default Show;
